import React from 'react';
import '@blueprintjs/core/lib/css/blueprint.css';
import '@blueprintjs/table/lib/css/table.css';
import { Column, Cell, Table2 } from '@blueprintjs/table';

export interface IWorkerSchedule {
	WO: string;
	client_name: string;
	platform_name: string;
	worker_name: string;
	operation_role: string;
	boarding_number: string;
	scheduled_start_date: string;
	scheduled_end_date: string;
	events: string[];
}

export interface IScheduleViewOutput {
	workers: IWorkerSchedule[];
	days: string[];
}

interface ScheduleViewTableProps {
	data: IWorkerSchedule[];
	days: string[]; // e.g. ['2025-06-01', '2025-06-02', ...]
	isFullScreen: boolean;
}

function extractInteger(input: string): number | null {
	const cleaned = input.split('*')[0].split('-')[0].split('\n')[0].trim();
	const n = parseInt(cleaned, 10);
	return Number.isNaN(n) ? null : n;
}

function getFormattedDate(date: string) {
	const [y, m, d] = date.split('-');
	const dt = new Date(`${y}-${m}-${d}T00:00:00`);
	const daysOfWeek = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
	return { dayOfWeek: daysOfWeek[dt.getDay()], dayMonth: `${d}/${m}` };
}

function formatToDayMonth(isoDate: string): string {
	if (!isoDate) {
		return '';
	}
	if (isoDate === '---') {
		return isoDate;
	}  
	const parts = isoDate.split('-');
	if (parts.length !== 3) {
		throw new Error(
			`Formato inválido: esperado YYYY-MM-DD, recebeu "${isoDate}"`
		);
	}
	const [year, month, day] = parts;
	return `${day}/${month}`;
}

const ScheduleViewTable: React.FC<ScheduleViewTableProps> = ({
	data,
	days,
	isFullScreen,
}) => {
	const dateCols = days.filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d));

	const columns = [
		<Column
			key="wo"
			name="WO"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].WO}
					</Cell>
				);
			}}
		/>,
		<Column
			key="client"
			name="Cliente"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].client_name}
					</Cell>
				);
			}}
		/>,
		<Column
			key="platform"
			name="Plataforma"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].platform_name}
					</Cell>
				);
			}}
		/>,
		<Column
			key="role"
			name="Função"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].operation_role}
					</Cell>
				);
			}}
		/>,
		<Column
			key="worker"
			name="Trabalhador"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].worker_name}
					</Cell>
				);
			}}
		/>,
		<Column
			key="boarding_number"
			name="Nº E"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{data[rowIndex].boarding_number}
					</Cell>
				);
			}}
		/>,
		<Column
			key="scheduled_start_date"
			name="Início"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{formatToDayMonth(data[rowIndex].scheduled_start_date)}
					</Cell>
				);
			}}
		/>,
		<Column
			key="scheduled_end_date"
			name="Fim"
			cellRenderer={rowIndex => {
				const isSum =
					data[rowIndex].worker_name === 'SOMA TOTAL' ||
					data[rowIndex].worker_name === 'SOMA PROG';
				const bg = isSum ? '#D3D3D3' : undefined;
				return (
					<Cell
						style={{
							backgroundColor: bg,
							padding: '4px',
							fontWeight: isSum ? 'bold' : undefined,
						}}
					>
						{formatToDayMonth(data[rowIndex].scheduled_end_date)}
					</Cell>
				);
			}}
		/>,
		...dateCols.map((day, i) => {
			const { dayOfWeek, dayMonth } = getFormattedDate(day);
			return (
				<Column
					key={day}
					name=""
					columnHeaderCellRenderer={() => (
						<div style={{ textAlign: 'center' }}>
							<div>{dayOfWeek}</div>
							<div>{dayMonth}</div>
						</div>
					)}
					cellRenderer={rowIndex => {
						const isSum =
							data[rowIndex].worker_name === 'SOMA TOTAL' ||
							data[rowIndex].worker_name === 'SOMA PROG';
						const raw = data[rowIndex].events[i] || '';
						let bg: string | undefined;
						let color: string | undefined;
						let weight: string | undefined;

						if (isSum) {
							// mantém lógica de soma
							const cleaned = raw.split('*')[0].split('-')[0].split('\n')[0];
							if (cleaned && cleaned !== '0') {
								bg = '#6b6a6a';
								color = '#FFFFFF';
								weight = 'bold';
							} else {
								bg = '#D3D3D3';
								color = '#000000';
								weight = 'bold';
							}
						} else {
							// nova regra de event
							if (raw.includes('*')) {
								bg = '#001D55';
								color = '#FFFFFF';
								// } else if (raw.includes('*')) {
								// 	bg = '#DFF2FA';
								// 	color = '#000000';
							} else {
								bg = undefined; // branco default
								color = '#000000';
							}
						}

						return (
							<Cell
								style={{
									backgroundColor: bg,
									color,
									textAlign: 'center',
									padding: '8px 4px',
									lineHeight: '1.1',
									fontWeight: weight,
								}}
							>
								{raw}
							</Cell>
						);
					}}
				/>
			);
		}),
	];

	return (
		<div
			style={{
				width: isFullScreen ? '100%' : 1545,
				height: isFullScreen ? 'calc(100vh - 100px)' : 600,
			}}
		>
			<Table2
				numRows={data.length}
				numFrozenColumns={8}
				columnWidths={[
					50,
					80,
					120,
					150,
					180,
					50,
					55,
					55,
					...dateCols.map(() => 75),
				]}
				defaultRowHeight={27}
			>
				{columns}
			</Table2>
		</div>
	);
};

export default ScheduleViewTable;
